import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Upload } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ContactsFilterBar from '../../components/contacts/ContactsFilterBar';
import type { FilterState } from '../../components/contacts/ContactsFilterBar';
import ContactsTable from '../../components/contacts/ContactsTable';
import type { AccountGroup } from '../../components/contacts/ContactsTable';
import ContactDetailDrawer from '../../components/contacts/ContactDetailDrawer';
import ContactsCsvUploadModal from '../../components/contacts/ContactsCsvUploadModal';
import BatchGenerationBar from '../../components/contacts/BatchGenerationBar';
import type { ContactWithAccount, Account } from '../../types';

export default function Contacts() {
  const { user, organisation } = useAuth();
  const { apiGet } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [contacts, setContacts] = useState<ContactWithAccount[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState<ContactWithAccount | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [objectiveContactIds, setObjectiveContactIds] = useState<Set<string>>(new Set());

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    accounts: [],
    seniority: '',
    department: '',
    outreachStatus: '',
    assignedTo: '',
  });

  const loadData = useCallback(async () => {
    if (!organisation || !user) return;
    setLoading(true);

    // Cross-account contacts read (migrated): the backend scopes accounts/contacts
    // to those the caller can_access_account (admin → all org; leader → assigned∪team;
    // member → team) and enriches each contact with account_company_name/account_tier/
    // assigned_user_name, reproducing the legacy RLS-filtered result set.
    let accts: Account[] = [];
    let enriched: ContactWithAccount[] = [];
    try {
      const data = await apiGet<{ accounts: Account[]; contacts: ContactWithAccount[] }>(
        `/v1/contacts?org_id=${organisation.id}`,
      );
      accts = data.accounts || [];
      enriched = data.contacts || [];
    } catch {
      accts = [];
      enriched = [];
    }

    setAccounts(accts);
    setContacts(enriched);

    // Mission Control objective highlighting (migrated): the caller's current cycle —
    // already enriched with objectives → objective-contacts — comes back from the signed
    // AWS overview endpoint (scoped server-side to org_id + the authenticated user). We
    // only highlight when that cycle is in an active state, matching the legacy filter.
    try {
      const mc = await apiGet<{
        current_cycle: {
          status?: string;
          objectives?: { contacts?: { contact_id?: string }[] }[];
        } | null;
      }>(`/v1/mission-control/overview?org_id=${organisation.id}`);
      const cycle = mc.current_cycle;
      if (cycle && ['submitted', 'approved', 'in_progress'].includes(cycle.status ?? '')) {
        const ids = new Set<string>();
        for (const obj of cycle.objectives ?? []) {
          for (const oc of obj.contacts ?? []) {
            if (oc.contact_id) ids.add(oc.contact_id);
          }
        }
        setObjectiveContactIds(ids);
      } else {
        setObjectiveContactIds(new Set());
      }
    } catch {
      setObjectiveContactIds(new Set());
    }

    const contactIdParam = searchParams.get('contactId');
    if (contactIdParam) {
      const match = enriched.find((c: ContactWithAccount) => c.id === contactIdParam);
      if (match) setSelectedContact(match);
      setSearchParams({}, { replace: true });
    }

    setLoading(false);
  }, [organisation, user, apiGet]);

  useEffect(() => { loadData(); }, [loadData]);

  const teamMembers = useMemo(() => {
    const map = new Map<string, string>();
    contacts.forEach(c => {
      if (c.assigned_to && c.assigned_user_name) {
        map.set(c.assigned_to, c.assigned_user_name);
      }
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [contacts]);

  const accountOptions = useMemo(() =>
    accounts.map(a => ({ id: a.id, name: a.company_name })),
    [accounts]
  );

  const filtered = useMemo(() => {
    let result = contacts;

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
        c.role?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
      );
    }

    if (filters.accounts.length > 0) {
      const set = new Set(filters.accounts);
      result = result.filter(c => set.has(c.account_id));
    }

    if (filters.seniority) {
      result = result.filter(c => c.seniority === filters.seniority);
    }

    if (filters.outreachStatus) {
      const now = Date.now();
      result = result.filter(c => {
        const lastContact = c.last_contacted_at ? new Date(c.last_contacted_at).getTime() : 0;
        const daysSince = lastContact ? Math.floor((now - lastContact) / (1000 * 60 * 60 * 24)) : -1;
        switch (filters.outreachStatus) {
          case 'none': return daysSince === -1;
          case 'recent_7': return daysSince >= 0 && daysSince <= 7;
          case 'recent_30': return daysSince >= 0 && daysSince <= 30;
          case 'stale': return daysSince > 30;
          default: return true;
        }
      });
    }

    if (filters.assignedTo) {
      if (filters.assignedTo === 'me') {
        result = result.filter(c => c.assigned_to === user?.id);
      } else if (filters.assignedTo === 'unassigned') {
        result = result.filter(c => !c.assigned_to);
      } else {
        result = result.filter(c => c.assigned_to === filters.assignedTo);
      }
    }

    return result;
  }, [contacts, filters, user]);

  const groups: AccountGroup[] = useMemo(() => {
    const map = new Map<string, ContactWithAccount[]>();
    for (const c of filtered) {
      const existing = map.get(c.account_id) || [];
      existing.push(c);
      map.set(c.account_id, existing);
    }

    return [...map.entries()]
      .map(([accountId, contactsList]) => {
        const sorted = objectiveContactIds.size > 0
          ? [...contactsList].sort((a, b) => {
              const aObj = objectiveContactIds.has(a.id) ? 0 : 1;
              const bObj = objectiveContactIds.has(b.id) ? 0 : 1;
              return aObj - bObj;
            })
          : contactsList;
        return {
          accountId,
          accountName: sorted[0]?.account_company_name || '',
          tier: sorted[0]?.account_tier || '',
          contacts: sorted,
        };
      })
      .sort((a, b) => a.accountName.localeCompare(b.accountName));
  }, [filtered, objectiveContactIds]);

  const allSelected = filtered.length > 0 && filtered.every(c => selectedIds.has(c.id));

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(c => c.id)));
    }
  };

  const selectedContacts = useMemo(() =>
    contacts.filter(c => selectedIds.has(c.id)),
    [contacts, selectedIds]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="mb-6 flex flex-col gap-5 border-b border-app-line pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-app-accent text-app-accent-text">
            <Users className="w-5 h-5 text-forest-600" />
          </div>
          <div>
            <p className="app-kicker mb-1">Stakeholder execution</p>
            <h1 className="font-display text-3xl font-semibold text-app-ink sm:text-4xl">Contacts</h1>
            <p className="text-sm text-app-muted">
              {contacts.length} contacts across {accounts.length} accounts
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="btn-primary gap-2"
        >
          <Upload className="w-4 h-4" />
          Upload Contacts
        </button>
      </div>

      <div className="mb-6">
        <ContactsFilterBar
          filters={filters}
          onChange={setFilters}
          accountOptions={accountOptions}
          teamMembers={teamMembers}
          currentUserId={user?.id || ''}
        />
      </div>

      <div className="mb-2 text-xs text-app-muted">
        Showing {filtered.length} of {contacts.length} contacts
      </div>

      <ContactsTable
        groups={groups}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        allSelected={allSelected}
        onContactClick={setSelectedContact}
        totalCount={filtered.length}
        highlightedContactIds={objectiveContactIds}
      />

      {selectedContact && (
        <ContactDetailDrawer
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onUpdate={loadData}
        />
      )}

      {showUpload && (
        <ContactsCsvUploadModal
          accounts={accounts}
          onClose={() => setShowUpload(false)}
          onComplete={loadData}
        />
      )}

      <BatchGenerationBar
        selectedContacts={selectedContacts}
        onClear={() => setSelectedIds(new Set())}
        onComplete={() => { setSelectedIds(new Set()); loadData(); }}
      />
    </div>
  );
}
